import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Logo1, Logo2, Logo3, Logo4, Logo5, Logo6, Logo7 } from '../components/Logo';

const LogoPreview = () => {
  const [selectedLogo, setSelectedLogo] = useState(1);

  const logos = [
    { id: 1, name: 'Circular Airflow', component: Logo1, description: 'Dynamic circular airflow with center dot' },
    { id: 2, name: 'Eco Leaf Flow', component: Logo2, description: 'Leaf shape with airflow lines - eco-friendly' },
    { id: 3, name: 'Snowflake Cool', component: Logo3, description: 'Snowflake design representing cooling' },
    { id: 4, name: 'Minimal AC Unit', component: Logo4, description: 'Clean AC unit with air waves' },
    { id: 5, name: 'Bold K Monogram', component: Logo5, description: 'Modern K letter with cooling elements' },
    { id: 6, name: 'Wind Propeller', component: Logo6, description: 'Three-blade propeller for airflow' },
    { id: 7, name: 'Shield Protection', component: Logo7, description: 'Shield with cooling waves - trust & quality' }
  ];

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 bg-gray-50">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Logo Preview - Koctech Klima</h1>
          <p className="text-xl text-gray-600">
            Temporary SVG placeholder logos. Click to preview in header.
          </p>
          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 max-w-2xl mx-auto">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> These are temporary CSS/SVG placeholders. For professional logos, 
              please use Fiverr, 99designs, or Canva Pro.
            </p>
          </div>
        </div>

        {/* Logo Grid */}
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6 mb-12">
          {logos.map((logo) => {
            const LogoComponent = logo.component;
            return (
              <Card 
                key={logo.id}
                className={`cursor-pointer transition-all duration-300 hover:shadow-xl ${
                  selectedLogo === logo.id ? 'ring-4 ring-teal-500' : ''
                }`}
                onClick={() => setSelectedLogo(logo.id)}
              >
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <LogoComponent className="w-24 h-24" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{logo.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{logo.description}</p>
                  <Button 
                    size="sm" 
                    className={`w-full ${
                      selectedLogo === logo.id 
                        ? 'bg-teal-500 hover:bg-teal-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {selectedLogo === logo.id ? 'Selected' : 'Select'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Header Preview */}
        <Card className="mb-12 border-none shadow-xl">
          <CardHeader>
            <CardTitle>Header Preview - Option {selectedLogo}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-12 h-12" })}
                  <div>
                    <span className="font-bold text-xl text-gray-900">Koctech</span>
                    <span className="block text-sm text-teal-600 font-medium">İklimlendirme</span>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Ana Sayfa</span>
                  <span className="text-sm text-gray-600">Ürünler</span>
                  <span className="text-sm text-gray-600">Hizmetler</span>
                  <Button size="sm" className="bg-teal-500 hover:bg-teal-600 text-white">
                    İletişim
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Size Variations */}
        <Card className="mb-12 border-none shadow-xl">
          <CardHeader>
            <CardTitle>Size Variations - Option {selectedLogo}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-8 items-center">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Small (32px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-8 h-8 mx-auto" })}
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Medium (48px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-12 h-12 mx-auto" })}
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Large (96px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-24 h-24 mx-auto" })}
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">X-Large (128px)</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-32 h-32 mx-auto" })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Color Backgrounds */}
        <Card className="border-none shadow-xl">
          <CardHeader>
            <CardTitle>Background Tests - Option {selectedLogo}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white border p-8 rounded-lg text-center">
                <p className="text-sm text-gray-600 mb-4">On White</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-20 h-20 mx-auto" })}
              </div>
              <div className="bg-gray-900 p-8 rounded-lg text-center">
                <p className="text-sm text-gray-300 mb-4">On Dark</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-20 h-20 mx-auto" })}
              </div>
              <div className="bg-teal-500 p-8 rounded-lg text-center">
                <p className="text-sm text-white mb-4">On Brand Color</p>
                {React.createElement(logos.find(l => l.id === selectedLogo).component, { className: "w-20 h-20 mx-auto" })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <div className="mt-12 bg-blue-50 border-l-4 border-blue-400 p-6 rounded">
          <h3 className="font-bold text-blue-900 mb-3">Next Steps:</h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>Choose your favorite logo option from above</li>
            <li>For production use, get a professional logo designed on:
              <ul className="list-disc list-inside ml-6 mt-1">
                <li><strong>Fiverr</strong> - Quick & affordable ($5-$50)</li>
                <li><strong>99designs</strong> - Logo contest with multiple options</li>
                <li><strong>Canva Pro</strong> - DIY with professional templates</li>
              </ul>
            </li>
            <li>Tell me which option you like, and I'll apply it to your website temporarily</li>
            <li>Replace with your professional logo once ready</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default LogoPreview;
